import { BankruptcyChecklistResponse } from "../../../api/ClientFileApi";
import { ChecklistStatus, QuestionState } from "../../BankruptcyChecklist.types";
import { computeChecklistStatus } from "../../BankruptcyChecklist.utils";
import { DangerousDebtsFormValues } from "./DangerousDebts.types";

export const EMPTY_DANGEROUS_DEBTS: DangerousDebtsFormValues = {
  hasRentArrears: null,
  hasUtilityArrears: null,
  hasOpenPenalties: null,
  hasInstallmentAgreement: null,
  canMeetInstallmentAgreement: null,
  hasChildrenWithMaintenance: null,
  paysFullMaintenance: null,
  isMaintenanceGarnished: null,
};

export function mapResponseToDangerousDebts(response: BankruptcyChecklistResponse): DangerousDebtsFormValues {
  return {
    hasRentArrears: response.hasRentArrears,
    hasUtilityArrears: response.hasUtilityArrears,
    hasOpenPenalties: response.hasOpenPenalties,
    hasInstallmentAgreement: response.hasInstallmentAgreement,
    canMeetInstallmentAgreement: response.canMeetInstallmentAgreement,
    hasChildrenWithMaintenance: response.hasChildrenWithMaintenance,
    paysFullMaintenance: response.paysFullMaintenance,
    isMaintenanceGarnished: response.isMaintenanceGarnished,
  };
}

export type DangerousDebtsQuestionStates = Record<keyof DangerousDebtsFormValues, QuestionState>;

export function getQuestionStates(values: DangerousDebtsFormValues): DangerousDebtsQuestionStates {
  const installmentAgreementVisible = values.hasOpenPenalties === true;
  const canMeetAgreementVisible = installmentAgreementVisible && values.hasInstallmentAgreement === true;
  const fullMaintenanceVisible = values.hasChildrenWithMaintenance === true;
  const garnishedVisible = fullMaintenanceVisible && values.paysFullMaintenance === false;

  return {
    hasRentArrears: { visible: true, isWarning: values.hasRentArrears === true },
    hasUtilityArrears: { visible: true, isWarning: values.hasUtilityArrears === true },
    hasOpenPenalties: { visible: true, isWarning: false },
    hasInstallmentAgreement: {
      visible: installmentAgreementVisible,
      isWarning: values.hasInstallmentAgreement === false,
    },
    canMeetInstallmentAgreement: {
      visible: canMeetAgreementVisible,
      isWarning: values.canMeetInstallmentAgreement === false,
    },
    hasChildrenWithMaintenance: { visible: true, isWarning: false },
    paysFullMaintenance: { visible: fullMaintenanceVisible, isWarning: false },
    isMaintenanceGarnished: { visible: garnishedVisible, isWarning: values.isMaintenanceGarnished === false },
  };
}

export function getDangerousDebtsStatus(values: DangerousDebtsFormValues): ChecklistStatus {
  return computeChecklistStatus(getQuestionStates(values), values);
}

export function clearUnreachableAnswers(values: DangerousDebtsFormValues): DangerousDebtsFormValues {
  const next = { ...values };

  if (next.hasOpenPenalties !== true) {
    next.hasInstallmentAgreement = null;
  }
  if (next.hasOpenPenalties !== true || next.hasInstallmentAgreement !== true) {
    next.canMeetInstallmentAgreement = null;
  }
  if (next.hasChildrenWithMaintenance !== true) {
    next.paysFullMaintenance = null;
  }
  if (next.hasChildrenWithMaintenance !== true || next.paysFullMaintenance !== false) {
    next.isMaintenanceGarnished = null;
  }

  return next;
}

export function applyAnswer(
  values: DangerousDebtsFormValues,
  key: keyof DangerousDebtsFormValues,
  value: boolean | null
): DangerousDebtsFormValues {
  return clearUnreachableAnswers({ ...values, [key]: value });
}
